import {
  doc, getDoc, setDoc, updateDoc, addDoc, collection,
  query, where, getDocs, writeBatch
} from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { db } from './init';

export async function createSubmission(formData) {
  try {
    await addDoc(collection(db, 'submissions'), formData);
    return true;
  } catch (error) {
    console.error('Error creating document: ', error);
    return false;
  }
}

export async function updateSubmission(recordId, updatedData) {
  try {
    const recordRef = doc(db, 'submissions', recordId);
    await updateDoc(recordRef, updatedData);
    return true;
  } catch (error) {
    console.error(`Error updating record ${recordId}: `, error);
    return false;
  }
}

// Zoeken op rugnummer, met optioneel event filter (gebruikt door upload/display views)
export async function getSubmissionByStartNumber(startNumber, eventId = null) {
  try {
    const baseQuery = query(
      collection(db, 'submissions'),
      where('start_number', '==', startNumber)
    );

    const q = eventId
      ? query(baseQuery, where('event_id', '==', eventId))
      : baseQuery;

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) return [];

    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error retrieving document: ', error);
  }
}

// Zoeken op rugnummer + verplicht event ID (gebruikt door scherm-views)
export async function getSubmissionsByStartNumber(startNumber, event_id) {
  try {
    const q = query(
      collection(db, 'submissions'),
      where('start_number', '==', startNumber),
      where('event_id', '==', event_id)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) return null;

    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error retrieving document: ', error);
  }
}

export async function getSubmissionCountByEventId(eventId) {
  try {
    const q = query(collection(db, 'submissions'), where('event_id', '==', eventId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error counting submissions by event ID: ', error);
    return 0;
  }
}

export async function deleteSubmissionsByEventId(eventId) {
  try {
    const q = query(collection(db, 'submissions'), where('event_id', '==', eventId));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.log(`Geen submissions gevonden voor event_id: ${eventId}`);
      return 0;
    }

    const BATCH_SIZE = 500;
    const docs = snapshot.docs;
    let deleted = 0;

    for (let i = 0; i < docs.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      docs.slice(i, i + BATCH_SIZE).forEach(d => batch.delete(d.ref));
      await batch.commit();
      deleted += Math.min(BATCH_SIZE, docs.length - i);
    }

    console.log(`${deleted} submissions verwijderd voor event_id: ${eventId}`);
    return deleted;
  } catch (error) {
    console.error('Fout bij verwijderen van submissions:', error);
    throw error;
  }
}

export async function resetWatchedSubmissions(eventId) {
  try {
    if (!eventId) throw new Error('Event ID is required.');

    const q = query(
      collection(db, 'submissions'),
      where('event_id', '==', String(eventId))
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) throw new Error(`No submissions for event ${eventId}`);

    await Promise.all(
      querySnapshot.docs.map(doc => updateSubmission(doc.id, { watched: false }))
    );

    console.log(`Alle submissions voor event ${eventId} gereset`);
  } catch (error) {
    console.error('Error resetting watched submissions: ', error);
  }
}

export async function getUniqueEventIds() {
  try {
    const snapshot = await getDocs(collection(db, 'submissions'));
    const counts = {};
    for (const d of snapshot.docs) {
      const id = d.data().event_id;
      if (id) counts[id] = (counts[id] ?? 0) + 1;
    }
    return Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([id, count]) => ({ id, count }));
  } catch (e) {
    console.error('getUniqueEventIds failed:', e.message);
    throw e;
  }
}

export async function getAnalyticsData(eventId = null) {
  try {
  const q = eventId
    ? query(collection(db, 'submissions'), where('event_id', '==', eventId))
    : collection(db, 'submissions');
  const snapshot = await getDocs(q);
  const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

  const perEvent = {};
  let watched = 0;
  let withVideo = 0;
  let withPhoto = 0;
  let textOnly = 0;
  const startNumbers = new Set();

  for (const d of docs) {
    // per event
    const eid = d.event_id || 'onbekend';
    if (!perEvent[eid]) perEvent[eid] = { total: 0, watched: 0 };
    perEvent[eid].total++;
    if (d.watched) { perEvent[eid].watched++; watched++; }

    // media type
    // media type
    if (d.video) withVideo++;
    else if (d.image) withPhoto++;
    else textOnly++;

    // unique runners
    if (d.start_number) startNumbers.add(d.start_number);
  }

  const eventLabels = Object.keys(perEvent).sort();
  return {
    total: docs.length,
    eventCount: eventLabels.length,
    uniqueRunners: startNumbers.size,
    watchedPct: docs.length ? Math.round((watched / docs.length) * 100) : 0,
    perEvent: {
      labels: eventLabels,
      totals: eventLabels.map(e => perEvent[e].total),
      watched: eventLabels.map(e => perEvent[e].watched),
      unwatched: eventLabels.map(e => perEvent[e].total - perEvent[e].watched),
    },
    photoStats: withPhoto + textOnly > 0 ? { withVideo, withPhoto, textOnly } : null,
  };
  } catch (e) {
    console.error('getAnalyticsData failed:', e.message);
    throw e;
  }
}

export async function getSubmissionsByEventId(eventId) {
  try {
    const q = query(collection(db, 'submissions'), where('event_id', '==', eventId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.error('getSubmissionsByEventId failed:', e.message);
    throw e;
  }
}

export async function exportSubmissionsToExcel() {
  try {
    const querySnapshot = await getDocs(query(collection(db, 'submissions')));

    if (querySnapshot.empty) {
      console.warn('Geen submissions gevonden.');
      return;
    }

    const submissions = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    const worksheet = XLSX.utils.json_to_sheet(submissions);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Submissions');

    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'submissions.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log('Excel-bestand gegenereerd en gedownload.');
  } catch (error) {
    console.error('Fout bij exporteren van submissions: ', error);
  }
}
